/**
 * Sound effects. No music, and no audio files shipped — but no ad-hoc
 * synthesis at play time any more either.
 *
 * Every event sound is an **SFX Lab recipe**: a JSON document under `src/sfx/`
 * describing a deterministic little synthesizer patch — contact transient,
 * tuned body, sub weight, sparkle, air — plus its own space and dynamics. The
 * game renders each recipe once through `sfx-engine.js` (the lab's renderer,
 * ported) into an AudioBuffer, caches it, and plays it from there. The files
 * are the source of truth exactly the way `src/effects/*.json` is for the
 * particles: open one in SFX Lab, audition it, edit it, export it, and drop it
 * back under the same name. `tools/verify-sfx.mjs` keeps the folder honest.
 *
 * What playing from rendered buffers loses — a fresh waveform every event —
 * the recipes' `variation` blocks give back: each play is humanized within the
 * bounds the recipe declares (a few cents of pitch, a few percent of gain, a
 * few milliseconds of timing), so repeats share an identity without sharing a
 * waveform. Pitched families additionally climb the ladder by playback rate.
 *
 * ---------------------------------------------------------------------------
 * Why it is always consonant
 * ---------------------------------------------------------------------------
 * Every pitched recipe draws its notes from one **major pentatonic** — the
 * A-major ladder below, pitch classes {A, B, C#, E, F#}. A pentatonic scale
 * has no semitones and no tritone, so *any* set of its notes sounds agreeable
 * together — which matters here because the game has no idea what it is about
 * to play. One well-placed stone can close a dozen formations, and their
 * sounds overlap with whatever the shatter cascade is doing underneath.
 * `verify-sfx` enforces the property on the recipes themselves, and the
 * rate-shifts this file applies move along the same ladder, so nothing can
 * drift off it in either place.
 *
 * Combos climb the ladder instead of repeating a note, so a bigger combo is
 * audibly bigger.
 *
 * ---------------------------------------------------------------------------
 * The room
 * ---------------------------------------------------------------------------
 * Recipes bake their own space — that is what makes a lab audition and the
 * in-game sound the same sound. On top of that, a convolution reverb on a send
 * gives everything a *shared* room to sit in: long, dark, generated the same
 * way as everything else (decaying noise, shaped dark). Each voice sends a
 * little of itself there, so twenty different recipes still read as one
 * cavern rather than twenty rooms playing at once. The send is per-voice,
 * because the cavern should be behind the clinks and not smeared over the low
 * thud of a piece being put back.
 *
 * A compressor sits across the master. A cascade can fire twenty voices inside
 * half a second and without it the sum clips into a buzz.
 *
 * ---------------------------------------------------------------------------
 * Three registers, deliberately separated
 * ---------------------------------------------------------------------------
 * Everything shares one room, so the only way to keep it legible is to keep
 * the layers out of each other's frequency range:
 *
 *   **gameplay** — the clinks, chimes and thuds the player caused. Mid and up,
 *   dry enough to feel close, and the only layer allowed to be loud.
 *
 *   **ambient twinkles** — a stone catching the light. The `ambient-twinkle`
 *   recipe, but at a fraction of the level and behind a lowpass, so it reads
 *   as coming from somewhere else in the building rather than competing with
 *   the gameplay for the same octave.
 *
 *   **ambient bass** — slow deep swells on their own clock, still synthesized
 *   live rather than from a recipe: they are weather, not events. Each one
 *   picks a fresh pitch and duration, arrives over seconds, and is rolled off
 *   so hard that nothing of it reaches the register either of the others
 *   occupies.
 *
 * That separation is what lets all three run at once without a mix pass: they
 * overlap in time constantly and hardly at all in frequency.
 *
 * The fireworks are the one sound built as a sequence rather than a single
 * gesture: the launch recipe, then the burst recipe a rocket's climb later,
 * then the crackle recipe just behind it. All of it is scheduled up front on
 * the audio clock — Web Audio takes absolute start times, so a salvo of a
 * dozen shells costs one call and needs no timers to stay in step with the
 * picture.
 */

import { renderRecipe } from './sfx-engine.js';
import { RECIPES } from './sfx/index.js';

/**
 * A major pentatonic, in Hz, from A3 up. Roughly two and a half octaves, which
 * is as far as a combo can climb before it stops sounding like the same
 * instrument.
 */
export const LADDER = [
  220, 246.94, 277.18, 329.63, 369.99, 440, 493.88, 554.37, 659.25, 739.99, 880, 987.77, 1108.73,
  1318.51, 1479.98, 1760,
];

/**
 * Rungs the pitched recipes are authored at, so rate-shifts land on the ladder
 * exactly: playing `gem-chime` at `note(12) / note(CHIME_RUNG)` sounds rung 12.
 * Change a recipe's root note in the lab and these must follow.
 */
const CHIME_RUNG = 10; // gem-chime: A5
const CLEAR_RUNG = 5; //  formation-clear: A4
const TWINKLE_RUNG = 12; // ambient-twinkle: C#6

/**
 * How dark and quiet the ambient twinkles are. They fire at any moment,
 * including underneath a clear, and at full level in the open they would argue
 * with the gameplay chimes over the same register — so they are dropped hard
 * and put behind a wall (see `muffle` in `#out`), which takes the strike off
 * the front and leaves only the body. That is what "somewhere else in the
 * building" sounds like.
 */
const TINKLE_GAIN = 0.05;
const TINKLE_MUFFLE = 560;

/**
 * Ambient bass: deep swells on their own slow clock, unrelated to anything the
 * player does. Two octaves below the ladder, so they are consonant with it by
 * construction — a drone off the scale is the one thing that could make the
 * whole design clash, which is the property the ladder exists to protect.
 */
const BASS_DROP_OCTAVES = 4;
const BASS_MIN_MS = 9000;
const BASS_MAX_MS = 21000;
/** The first one waits, so pressing Play is not answered by a foghorn. */
const BASS_FIRST_MS = 4500;

/**
 * How long a shell climbs before it bursts, in seconds.
 *
 * Matched by hand to the rockets layer in `combo-fireworks.json`: a 0.05s delay
 * plus 1.31-1.36s of flight before the rocket dies and triggers the burst. That
 * flight time is not free to choose — with the launch speed it is what puts the
 * burst just over the tip of the pyramid, which `verify-effects` enforces against
 * `src/fireworks.js` — so this follows it rather than the other way round. The bang
 * landing off the flash is very obvious.
 *
 * The `firework-launch` recipe's whistle is authored to die at about this long
 * at rate 1; `sfx_firework` stretches its playback rate to the climb it drew,
 * so the hiss always thins out right up to the bang.
 */
const SHELL_CLIMB_S = 1.39;

/** Gap between shells in a salvo. Matches `FIREWORK_STAGGER_MS` in `vfx.js`. */
const SHELL_STAGGER_S = 0.26;

/** Level at the master when the sound is on. `setMuted` ramps between this and 0. */
const MASTER_GAIN = 0.9;

/** Cavern: long, dark, with enough pre-delay to read as distance. */
const REVERB_SECONDS = 2.8;
const REVERB_DECAY = 2.6;
const REVERB_PREDELAY = 0.022;
const REVERB_TONE = 3200;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const note = (i) => LADDER[clamp(Math.round(i), 0, LADDER.length - 1)];
const rand = (a, b) => a + Math.random() * (b - a);

export class GameAudio {
  constructor() {
    this.ctx = null;
    this.muted = false;
    /** Rendered recipes, by id. Filled lazily and by `#prewarm`. */
    this.buffers = new Map();
    /** Ambient twinkles are throttled — see `tinkle`. */
    this.lastTinkle = 0;
    /** So is the hover tick, and the turn click. See their handlers. */
    this.lastHover = 0;
    this.lastTurn = 0;
    /** Pending ambient-bass timer, or null when the clock is not running. */
    this.ambienceTimer = null;
    /** Counts sounds played, for the smoke test to assert against. */
    this.played = 0;
  }

  /**
   * Bring the audio up. Must be called from inside a user gesture: browsers
   * refuse to start an AudioContext otherwise, and one constructed too early
   * lands in `suspended` and stays there. Safe to call on every gesture.
   */
  resume() {
    if (!this.ctx) {
      const Ctx = globalThis.AudioContext ?? globalThis.webkitAudioContext;
      if (!Ctx) return;
      try {
        this.ctx = new Ctx();
      } catch {
        return; // no audio available; the game is unaffected
      }
      this.#buildGraph();
      this.#prewarm();
    }
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    if (!this.muted) this.#startAmbience();
  }

  /* --------------------------------------------------------------- ambience */

  /**
   * Start the ambient bass clock. Idempotent, because `resume` runs on every
   * gesture and only the first one is allowed to do anything.
   */
  #startAmbience() {
    if (this.ambienceTimer !== null) return;
    this.#queueAmbience(BASS_FIRST_MS);
  }

  /**
   * One swell, then the next, at an interval picked fresh each time. A fixed
   * period would be audible as a period within about a minute — which is the
   * same reason every stone keeps its own sparkle clock over in `vfx.js`.
   */
  #queueAmbience(delay) {
    this.ambienceTimer = setTimeout(() => {
      this.ambienceTimer = null;
      if (!this.muted && this.ctx?.state === 'running') this.#deepTone();
      this.#queueAmbience(rand(BASS_MIN_MS, BASS_MAX_MS));
    }, delay);
  }

  /** Stop the ambient clock, so muting does not leave a swell queued. */
  stopAmbience() {
    if (this.ambienceTimer === null) return;
    clearTimeout(this.ambienceTimer);
    this.ambienceTimer = null;
  }

  /* -------------------------------------------------------------------- mute */

  /**
   * Turn the sound off, or back on.
   *
   * `muted` alone is not enough to go quiet. `play` checks it, so nothing *new*
   * starts — but this graph schedules ahead of itself: a firework books its bang a
   * second and a half out and a salvo books a dozen of them, so muting mid-
   * celebration would otherwise keep banging away at a game that has been told to
   * be quiet. Cutting the master takes those with it, and it is a short ramp rather
   * than a jump because dropping a gain to zero on a sounding voice is a click.
   *
   * The ambient clock is stopped as well, rather than left running with its output
   * discarded — there is no reason to hold a timer for a sound nobody will hear.
   */
  setMuted(muted) {
    this.muted = muted;
    if (!this.ctx) return;

    if (this.master) {
      const now = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(now);
      this.master.gain.setValueAtTime(this.master.gain.value, now);
      this.master.gain.linearRampToValueAtTime(muted ? 0 : MASTER_GAIN, now + 0.04);
    }

    if (muted) this.stopAmbience();
    else this.#startAmbience();
  }

  #buildGraph() {
    const ctx = this.ctx;

    this.master = ctx.createGain();
    // Muted at the master if the player left it off, so a graph built on the very
    // first gesture cannot let a sound through before anyone can react.
    this.master.gain.value = this.muted ? 0 : MASTER_GAIN;

    // Keeps a twelve-formation combo from clipping into a buzz.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 22;
    comp.ratio.value = 7;
    comp.attack.value = 0.004;
    comp.release.value = 0.16;

    this.master.connect(comp).connect(ctx.destination);

    // Reverb send. Pre-delay first, then the tail, then a lowpass so the
    // reflections come back darker than the source — which is what a big stone
    // room does and what stops the tail sounding like sheet metal.
    this.send = ctx.createGain();
    this.send.gain.value = 1;

    const predelay = ctx.createDelay(0.2);
    predelay.delayTime.value = REVERB_PREDELAY;

    const convolver = ctx.createConvolver();
    convolver.buffer = this.#impulse();

    const tone = ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = REVERB_TONE;

    this.send.connect(predelay).connect(convolver).connect(tone).connect(this.master);
  }

  /** Decaying stereo noise — a room, without a room to record. */
  #impulse() {
    const ctx = this.ctx;
    const len = Math.floor(ctx.sampleRate * REVERB_SECONDS);
    const buffer = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch += 1) {
      const data = buffer.getChannelData(ch);
      for (let i = 0; i < len; i += 1) {
        const t = i / len;
        // A soft nose on the front reads as a big room; a hard one reads as a
        // gunshot in a corridor.
        const swell = Math.min(1, t * 45);
        data[i] = (Math.random() * 2 - 1) * swell * (1 - t) ** REVERB_DECAY;
      }
    }
    return buffer;
  }

  /* ------------------------------------------------------------------ voices */

  /**
   * Routes a voice to the dry master and to the room, in whatever mix.
   *
   * `muffle` puts a wall in front of it. It sits *before* the dry/wet split on
   * purpose: a sound heard through a wall is dull in the room as well, and
   * filtering only the dry path leaves a bright reverb tail arriving from a
   * source that has no brightness left in it, which reads as two sounds rather
   * than one far-off one.
   */
  #out(source, { gain = 0.3, wet = 0.45, muffle = 0 } = {}) {
    const ctx = this.ctx;
    const level = ctx.createGain();
    level.gain.value = gain;

    if (muffle) {
      const wall = ctx.createBiquadFilter();
      wall.type = 'lowpass';
      wall.frequency.value = muffle;
      wall.Q.value = 0.7;
      source.connect(wall).connect(level);
    } else {
      source.connect(level);
    }

    const dry = ctx.createGain();
    dry.gain.value = 1 - wet * 0.5;
    level.connect(dry).connect(this.master);

    const wetGain = ctx.createGain();
    wetGain.gain.value = wet;
    level.connect(wetGain).connect(this.send);
    return level;
  }

  /**
   * The rendered AudioBuffer for a recipe, rendering it now if it has to.
   *
   * Rendering is pure synchronous math — a big recipe costs a few tens of
   * milliseconds once, then lives in the cache. `#prewarm` works through the
   * whole library in idle time right after the graph comes up, so in practice
   * the lazy path here only runs if a sound fires in the first moments of the
   * very first gesture.
   */
  #buffer(id) {
    let buffer = this.buffers.get(id);
    if (buffer) return buffer;
    const rendered = renderRecipe(RECIPES[id], this.ctx.sampleRate);
    buffer = this.ctx.createBuffer(2, rendered.length, rendered.sampleRate);
    rendered.channels.forEach((data, channel) => buffer.copyToChannel(data, channel));
    this.buffers.set(id, buffer);
    return buffer;
  }

  /** Render the whole library one recipe per tick, off the gesture's frame. */
  #prewarm() {
    const ids = Object.keys(RECIPES);
    const step = () => {
      const id = ids.shift();
      if (!id || !this.ctx) return;
      if (!this.buffers.has(id)) this.#buffer(id);
      setTimeout(step, 0);
    };
    setTimeout(step, 0);
  }

  /**
   * One recipe, played once.
   *
   * `gain` and `wet` are the game's mix decisions — how loud this event is and
   * how much of it goes to the shared room — kept out of the recipes so the
   * lab's audition stays a statement about the sound, not about the game.
   * `rate` shifts pitch (and duration with it) for the families that climb the
   * ladder. Unless `humanize` is off, every play drifts within the recipe's
   * own `variation` bounds, so repeats never land sample-identical.
   */
  #voice(id, { gain = 0.5, wet = 0.2, rate = 1, when = 0, muffle = 0, humanize = true } = {}) {
    const recipe = RECIPES[id];
    if (!recipe) return;

    if (humanize) {
      const v = recipe.variation ?? {};
      rate *= 2 ** ((rand(-1, 1) * (v.pitchCents || 0)) / 1200);
      gain *= 1 + (rand(-1, 1) * (v.gainPercent || 0)) / 100;
      when += rand(0, (v.timingMs || 0) / 1000);
    }

    const src = this.ctx.createBufferSource();
    src.buffer = this.#buffer(id);
    src.playbackRate.value = rate;

    this.#out(src, { gain, wet, muffle });
    const t0 = this.ctx.currentTime + Math.max(0, when);
    src.start(t0);
    this.played += 1;
  }

  /**
   * A deep ambient swell — the bottom of the building, two octaves under the
   * ladder.
   *
   * Three things make this weather rather than a note. It **arrives** instead of
   * starting: a couple of seconds of attack, because anything faster is a hit and
   * a hit is an event the player will look for a cause for. It carries a **sub**
   * an octave down and a **detuned partner** a fraction sharp, so two sines beat
   * slowly against each other and the pitch never sits perfectly still. And it is
   * **rolled off hard**, so what is left is pressure rather than tone — which is
   * also what keeps it out of the way of the twinkles it plays under, since they
   * have nothing left below `TINKLE_MUFFLE` and this has nothing above it.
   *
   * This is the one voice that stays synthesized live rather than rendered from
   * a recipe: a recipe is a fixed document and a swell is supposed to be weather
   * — fresh pitch, fresh length, fresh beating every time, on a clock nothing
   * else knows about.
   */
  #deepTone() {
    const ctx = this.ctx;
    const t0 = ctx.currentTime;
    const freq = note(rand(0, 4)) / BASS_DROP_OCTAVES;
    const dur = rand(5, 9);

    const bus = ctx.createGain();

    const dark = ctx.createBiquadFilter();
    dark.type = 'lowpass';
    dark.frequency.value = 190;
    dark.Q.value = 0.6;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.exponentialRampToValueAtTime(1, t0 + rand(1.1, 2.2));
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    bus.connect(dark).connect(env);
    // Little room on it. A 50Hz sine through a 2.8s cavern is mud, and the tail
    // is inaudible at this pitch anyway — the reverb is there for the clinks.
    this.#out(env, { gain: 0.09, wet: 0.22 });

    for (const [ratio, level] of [
      [0.5, 0.5],
      [1, 1],
      [1.005, 0.7],
      [2.01, 0.12],
    ]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq * ratio;

      const voice = ctx.createGain();
      voice.gain.value = level;

      osc.connect(voice).connect(bus);
      osc.start(t0);
      osc.stop(t0 + dur + 0.3);
    }
    this.played += 1;
  }

  /* ------------------------------------------------------------------ events */

  /**
   * @param {string} name what happened
   * @param {object} [opts]
   */
  play(name, opts = {}) {
    if (this.muted || !this.ctx || this.ctx.state !== 'running') return;
    const fn = this[`sfx_${name}`];
    if (typeof fn === 'function') fn.call(this, opts);
  }

  /**
   * A piece lifting out of the tray.
   *
   * The same gesture as `return`, in the other direction: the two recipes are
   * mirrored pairs (rising interval up, falling interval back), so picking up
   * and putting down read as one motion with two ends.
   */
  sfx_lift() {
    this.#voice('stone-lift', { gain: 0.4, wet: 0.16 });
  }

  /** Put back without placing. Dry and low — a non-event, deliberately. */
  sfx_return() {
    this.#voice('stone-return', { gain: 0.36, wet: 0.14 });
  }

  /**
   * The drop target moved onto a new set of cells. Soft and nearly dry: this
   * fires whenever the highlight changes, which during a drag across a face is
   * often, so it has to sit under the sound of the game rather than in it.
   */
  sfx_hover() {
    const now = this.ctx.currentTime;
    if (now - this.lastHover < 0.05) return;
    this.lastHover = now;
    this.#voice('aim-whisper', { gain: 0.16, wet: 0.1 });
  }

  /**
   * A piece seating into the stonework: weight first, then one gem chime per
   * stone, climbing the ladder — a run up the stones just set, longer for a
   * bigger piece.
   */
  sfx_place({ cells = 1 } = {}) {
    this.#voice('stone-place', { gain: 0.62, wet: 0.24 });
    for (let i = 0; i < Math.min(cells, 6); i += 1) {
      this.#voice('gem-chime', {
        gain: 0.15,
        wet: 0.4,
        when: 0.03 + i * 0.045,
        rate: note(7 + i * 2) / note(CHIME_RUNG),
      });
    }
  }

  /**
   * One completed formation being announced. `step` climbs the ladder two
   * rungs at a time, so a combo rises through its own arpeggio as the
   * formations are counted off. The bigger the shape, the more glint stacked
   * on top — at matching pitch, so the pile stays one chord.
   */
  sfx_clear({ step = 0, cells = 3 } = {}) {
    const rate = note(CLEAR_RUNG + step * 2) / note(CLEAR_RUNG);
    this.#voice('formation-clear', { gain: 0.58, wet: 0.35, rate });
    if (cells >= 5) this.#voice('gem-chime', { gain: 0.12, wet: 0.5, when: 0.12, rate });
    if (cells >= 9) {
      this.#voice('gem-chime', {
        gain: 0.1,
        wet: 0.55,
        when: 0.2,
        rate: rate * (note(CHIME_RUNG + 2) / note(CHIME_RUNG)),
      });
    }
  }

  /**
   * The stones going. The cascade recipe is the bed — the low break and the
   * wide glassy debris — and a scatter of rate-shifted chime clinks over the
   * first half second sells the count: a dozen gems hitting stone is a
   * cascade, and the irregular spacing is most of what makes it one.
   */
  sfx_shatter({ count = 3 } = {}) {
    this.#voice('gem-cascade', { gain: 0.55, wet: 0.4 });
    const clinks = clamp(count, 3, 9);
    for (let i = 0; i < clinks; i += 1) {
      this.#voice('gem-chime', {
        gain: rand(0.05, 0.1),
        wet: 0.55,
        when: rand(0, 0.42),
        rate: note(rand(8, 15)) / note(CHIME_RUNG),
      });
    }
  }

  /**
   * Several formations at once: the gold flourish over the cascade — and from
   * five up, the grand-combo crown underneath it, arriving a beat later so the
   * flourish announces and the crown lands.
   */
  sfx_combo({ count = 3 } = {}) {
    const size = clamp(count, 3, 6);
    this.#voice('combo-flourish', { gain: 0.5 + size * 0.04, wet: 0.4 });
    if (count >= 5) this.#voice('grand-combo', { gain: 0.6, wet: 0.5, when: 0.12 });
  }

  /**
   * Game over. The ladder walked down, and left in the room — and if the run
   * set a record, the fanfare blooms out of the last step of the descent, so
   * the bad news and the good news are one sentence.
   *
   * @param {{record?: boolean}} [opts]
   */
  sfx_gameOver({ record = false } = {}) {
    this.#voice('game-over', { gain: 0.65, wet: 0.5 });
    if (record) this.#voice('new-record', { gain: 0.6, wet: 0.5, when: 0.9 });
  }

  /**
   * A stone catching the light. Throttled hard: every filled cell keeps its own
   * sparkle clock, so at a full board this fires tens of times a second and
   * would be a swarm of insects rather than atmosphere. Rate-shifted around
   * the upper rungs so the building never twinkles the same note twice in a
   * row.
   */
  sfx_tinkle() {
    const now = this.ctx.currentTime;
    if (now - this.lastTinkle < 1.1) return;
    this.lastTinkle = now;
    this.#voice('ambient-twinkle', {
      gain: TINKLE_GAIN,
      wet: 0.85,
      muffle: TINKLE_MUFFLE,
      rate: note(rand(10, 15)) / note(TWINKLE_RUNG),
    });
  }

  /* --------------------------------------------------------------- the turn */

  /**
   * Taking hold of the pyramid. Weight plus a scrape — a hand landing on
   * stone, not a button being pressed, which is why its recipe carries no
   * pitch at all.
   */
  sfx_grab() {
    this.#voice('pyramid-grab', { gain: 0.32, wet: 0.14 });
  }

  /** Letting go: the same hand, lower and further into the room. */
  sfx_release() {
    this.#voice('pyramid-release', { gain: 0.28, wet: 0.2 });
  }

  /**
   * The pyramid passing under the hand. Very quiet, and throttled hard — a
   * pointer drag fires dozens of moves a second and a click on each is a buzz.
   *
   * Turning one way is one detent recipe and the other its brighter twin, so
   * which way the board is going is audible without watching it. The recipes'
   * own pitch variation keeps a long drag from sounding like one sample on
   * repeat.
   *
   * @param {{dir?: number}} [opts] `dir` negative for left, positive for right
   */
  sfx_turn({ dir = 1 } = {}) {
    const now = this.ctx.currentTime;
    if (now - this.lastTurn < 0.1) return;
    this.lastTurn = now;
    this.#voice(dir < 0 ? 'turn-tick-left' : 'turn-tick-right', { gain: 0.14, wet: 0.15 });
  }

  /**
   * One shell, start to finish: it goes up, then it goes off.
   *
   * `when` is when it *launches*, not when it bursts, because that is what the
   * caller knows — the visual is spawned at the same moment. The bang is placed
   * `SHELL_CLIMB_S` later, which is matched by hand to how long the rockets in
   * `combo-fireworks.json` take to die. The climb jitters a few percent per
   * shell, and the launch recipe is *stretched to match* by playback rate, so
   * its whistle always thins out right up to its own bang.
   *
   * @param {{when?: number}} [opts] seconds until this shell leaves the pad
   */
  sfx_firework({ when = 0 } = {}) {
    const climb = SHELL_CLIMB_S * rand(0.96, 1.04);
    this.#voice('firework-launch', {
      gain: 0.4,
      wet: 0.3,
      when,
      rate: SHELL_CLIMB_S / climb,
      humanize: false,
    });
    this.#voice('firework-burst', { gain: 0.85, wet: 0.4, when: when + climb });
    this.#voice('firework-crackle', { gain: 0.45, wet: 0.6, when: when + climb + 0.06 });
  }

  /**
   * A salvo: one shell per formation the move closed, fired in order.
   *
   * @param {{count?: number}} [opts] how many shells
   */
  sfx_fireworks({ count = 1 } = {}) {
    for (let i = 0; i < count; i += 1) {
      this.sfx_firework({ when: i * SHELL_STAGGER_S + rand(-0.03, 0.03) });
    }
  }

  /** UI. Small, and in the room like everything else. */
  sfx_click() {
    this.#voice('ui-click', { gain: 0.4, wet: 0.2 });
  }

  /** Starting a game: a short major flourish up the ladder. */
  sfx_start() {
    this.#voice('game-start', { gain: 0.6, wet: 0.35 });
  }
}
